"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { ArrowRight, CheckCircle2, AlertCircle, Info } from "lucide-react";
import type { Assessment, AssessmentResult, AssessmentResultBand } from "@/features/assessment/types";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
interface AssessmentQuizProps {
  assessment: Assessment;
  locale: AppLocale;
}
function computeResult(assessment: Assessment, answers: Record<string, number>): AssessmentResult {
  const totalScore = Object.values(answers).reduce((sum, v) => sum + v, 0);
  const band = assessment.resultBands.find(b => totalScore >= b.minScore && totalScore <= b.maxScore) ?? assessment.resultBands[assessment.resultBands.length - 1];
  return {
    assessmentId: assessment.id,
    slug: assessment.slug,
    totalScore,
    band
  };
}
const severityConfig: Record<AssessmentResultBand["severity"], {
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  minimal: {
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    icon: <CheckCircle2 className="h-6 w-6 text-emerald-600" />
  },
  mild: {
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    icon: <Info className="h-6 w-6 text-amber-600" />
  },
  moderate: {
    color: "text-orange-700",
    bg: "bg-orange-50 border-orange-200",
    icon: <AlertCircle className="h-6 w-6 text-orange-600" />
  },
  severe: {
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    icon: <AlertCircle className="h-6 w-6 text-red-600" />
  }
};
export function AssessmentQuiz(t0) {
  const $ = _c(106);
  const {
    assessment,
    locale
  } = t0;
  const [currentIndex, setCurrentIndex] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [answers, setAnswers] = useState(t1);
  const [result, setResult] = useState(null);
  const currentQuestion = assessment.questions[currentIndex];
  const progress = currentIndex / assessment.questions.length * 100;
  const isLast = currentIndex === assessment.questions.length - 1;
  const selectedValue = answers[currentQuestion?.id ?? ""];
  const hasAnswered = selectedValue !== undefined;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleSelect(questionId, value) {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleSelect = t2;
  let t3;
  if ($[2] !== answers || $[3] !== assessment || $[4] !== currentQuestion || $[5] !== hasAnswered || $[6] !== isLast || $[7] !== selectedValue) {
    t3 = function handleNext() {
      if (!hasAnswered) {
        return;
      }
      if (isLast) {
        setResult(computeResult(assessment, {
          ...answers,
          [currentQuestion.id]: selectedValue
        }));
      } else {
        setCurrentIndex(_temp);
      }
    };
    $[2] = answers;
    $[3] = assessment;
    $[4] = currentQuestion;
    $[5] = hasAnswered;
    $[6] = isLast;
    $[7] = selectedValue;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleNext = t3;
  if (result) {
    const config = severityConfig[result.band.severity];
    let t4;
    let t5;
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        opacity: 0,
        y: 20
      };
      t5 = {
        opacity: 1,
        y: 0
      };
      t6 = {
        duration: 0.6
      };
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t4 = $[9];
      t5 = $[10];
      t6 = $[11];
    }
    const t7 = `rounded-2xl border p-8 ${config.bg}`;
    const t8 = `text-xs font-bold tracking-[0.16em] uppercase ${config.color}`;
    let t9;
    if ($[12] !== t8) {
      t9 = <span className={t8}>Your Result</span>;
      $[12] = t8;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== config.icon || $[15] !== t9) {
      t10 = <div className="mb-4 flex items-center gap-3">{config.icon}{t9}</div>;
      $[14] = config.icon;
      $[15] = t9;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    const t11 = `mb-3 font-heading text-2xl font-semibold ${config.color}`;
    let t12;
    if ($[17] !== result.band.title || $[18] !== t11) {
      t12 = <h2 className={t11}>{result.band.title}</h2>;
      $[17] = result.band.title;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] !== result.band.description) {
      t13 = <p className="mb-6 text-sm leading-relaxed text-foreground">{result.band.description}</p>;
      $[20] = result.band.description;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    let t14;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="font-medium">Score:</span>;
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    const t15 = `font-bold ${config.color}`;
    let t16;
    if ($[23] !== result.totalScore || $[24] !== t15) {
      t16 = <span className={t15}>{result.totalScore}</span>;
      $[23] = result.totalScore;
      $[24] = t15;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    let t17;
    if ($[26] !== assessment.questions) {
      t17 = assessment.questions.reduce(_temp3, 0);
      $[26] = assessment.questions;
      $[27] = t17;
    } else {
      t17 = $[27];
    }
    let t18;
    if ($[28] !== t17) {
      t18 = <span>/{" "}{t17}</span>;
      $[28] = t17;
      $[29] = t18;
    } else {
      t18 = $[29];
    }
    let t19;
    if ($[30] !== t16 || $[31] !== t18) {
      t19 = <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">{t14}{t16}{t18}</div>;
      $[30] = t16;
      $[31] = t18;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <p className="mb-3 text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase">What to do next</p>;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    let t21;
    if ($[34] !== result.band.recommendations) {
      t21 = result.band.recommendations.map(_temp4);
      $[34] = result.band.recommendations;
      $[35] = t21;
    } else {
      t21 = $[35];
    }
    let t22;
    if ($[36] !== t21) {
      t22 = <div className="rounded-xl bg-card/70 p-5">{t20}<ul className="space-y-2">{t21}</ul></div>;
      $[36] = t21;
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    let t23;
    if ($[38] !== t10 || $[39] !== t12 || $[40] !== t13 || $[41] !== t19 || $[42] !== t22 || $[43] !== t7) {
      t23 = <div className={t7}>{t10}{t12}{t13}{t19}{t22}</div>;
      $[38] = t10;
      $[39] = t12;
      $[40] = t13;
      $[41] = t19;
      $[42] = t22;
      $[43] = t7;
      $[44] = t23;
    } else {
      t23 = $[44];
    }
    let t24;
    if ($[45] !== locale) {
      t24 = localizeHref(locale, "/signup/seeker");
      $[45] = locale;
      $[46] = t24;
    } else {
      t24 = $[46];
    }
    let t25;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <ArrowRight className="h-4 w-4" />;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    let t26;
    if ($[48] !== t24) {
      t26 = <Link href={t24} className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-primary/90 hover:shadow-md">Talk to a Therapist{t25}</Link>;
      $[48] = t24;
      $[49] = t26;
    } else {
      t26 = $[49];
    }
    let t27;
    if ($[50] !== locale) {
      t27 = localizeHref(locale, "/assessment");
      $[50] = locale;
      $[51] = t27;
    } else {
      t27 = $[51];
    }
    let t28;
    if ($[52] !== t27) {
      t28 = <Link href={t27} className="text-sm font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline">Try another assessment</Link>;
      $[52] = t27;
      $[53] = t28;
    } else {
      t28 = $[53];
    }
    let t29;
    if ($[54] !== t26 || $[55] !== t28) {
      t29 = <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row">{t26}{t28}</div>;
      $[54] = t26;
      $[55] = t28;
      $[56] = t29;
    } else {
      t29 = $[56];
    }
    let t30;
    if ($[57] !== t23 || $[58] !== t29) {
      t30 = <motion.div initial={t4} animate={t5} transition={t6} className="mx-auto max-w-2xl">{t23}{t29}</motion.div>;
      $[57] = t23;
      $[58] = t29;
      $[59] = t30;
    } else {
      t30 = $[59];
    }
    return t30;
  }
  const t4 = currentIndex + 1;
  let t5;
  if ($[60] !== assessment.questions.length || $[61] !== t4) {
    t5 = <span>Question {t4} of {assessment.questions.length}</span>;
    $[60] = assessment.questions.length;
    $[61] = t4;
    $[62] = t5;
  } else {
    t5 = $[62];
  }
  let t6;
  if ($[63] !== progress) {
    t6 = Math.round(progress);
    $[63] = progress;
    $[64] = t6;
  } else {
    t6 = $[64];
  }
  let t7;
  if ($[65] !== t6) {
    t7 = <span>{t6}%</span>;
    $[65] = t6;
    $[66] = t7;
  } else {
    t7 = $[66];
  }
  let t8;
  if ($[67] !== t5 || $[68] !== t7) {
    t8 = <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">{t5}{t7}</div>;
    $[67] = t5;
    $[68] = t7;
    $[69] = t8;
  } else {
    t8 = $[69];
  }
  const t9 = `${progress}%`;
  let t10;
  if ($[70] !== t9) {
    t10 = {
      width: t9
    };
    $[70] = t9;
    $[71] = t10;
  } else {
    t10 = $[71];
  }
  let t11;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.4
    };
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  let t12;
  if ($[73] !== t10) {
    t12 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><motion.div className="h-full rounded-full bg-primary" animate={t10} transition={t11} /></div>;
    $[73] = t10;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== t12 || $[76] !== t8) {
    t13 = <div className="mb-8">{t8}{t12}</div>;
    $[75] = t12;
    $[76] = t8;
    $[77] = t13;
  } else {
    t13 = $[77];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      x: 20
    };
    t15 = {
      opacity: 1,
      x: 0
    };
    t16 = {
      opacity: 0,
      x: -20
    };
    t17 = {
      duration: 0.3
    };
    $[78] = t14;
    $[79] = t15;
    $[80] = t16;
    $[81] = t17;
  } else {
    t14 = $[78];
    t15 = $[79];
    t16 = $[80];
    t17 = $[81];
  }
  let t18;
  if ($[82] !== currentQuestion.text) {
    t18 = <h2 className="mb-8 font-heading text-xl leading-snug font-medium text-foreground sm:text-2xl">{currentQuestion.text}</h2>;
    $[82] = currentQuestion.text;
    $[83] = t18;
  } else {
    t18 = $[83];
  }
  let t19;
  if ($[84] !== answers || $[85] !== currentQuestion.id || $[86] !== currentQuestion.options) {
    let t20;
    if ($[88] !== answers || $[89] !== currentQuestion.id) {
      t20 = option => {
        const isSelected = answers[currentQuestion.id] === option.value;
        return <button key={option.id} onClick={() => handleSelect(currentQuestion.id, option.value)} className={`group flex items-center gap-4 rounded-xl border px-5 py-4 text-left text-sm font-medium transition-all duration-200 ${isSelected ? "border-primary bg-primary/5 text-primary" : "border-border bg-card text-foreground hover:border-border hover:bg-muted"}`}><span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${isSelected ? "border-primary bg-primary" : "border-border group-hover:border-primary/40"}`}>{isSelected && <span className="h-2 w-2 rounded-full bg-white" />}</span>{option.label}</button>;
      };
      $[88] = answers;
      $[89] = currentQuestion.id;
      $[90] = t20;
    } else {
      t20 = $[90];
    }
    t19 = currentQuestion.options.map(t20);
    $[84] = answers;
    $[85] = currentQuestion.id;
    $[86] = currentQuestion.options;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  let t20;
  if ($[91] !== t19) {
    t20 = <div className="flex flex-col gap-3">{t19}</div>;
    $[91] = t19;
    $[92] = t20;
  } else {
    t20 = $[92];
  }
  const t21 = !hasAnswered;
  const t22 = isLast ? "See my results" : "Next question";
  let t23;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ArrowRight className="h-4 w-4" />;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== handleNext || $[95] !== t21 || $[96] !== t22) {
    t24 = <div className="mt-8"><button onClick={handleNext} disabled={t21} className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40">{t22}{t23}</button></div>;
    $[94] = handleNext;
    $[95] = t21;
    $[96] = t22;
    $[97] = t24;
  } else {
    t24 = $[97];
  }
  let t25;
  if ($[98] !== currentIndex || $[99] !== t18 || $[100] !== t20 || $[101] !== t24) {
    t25 = <AnimatePresence mode="wait"><motion.div key={currentIndex} initial={t14} animate={t15} exit={t16} transition={t17}>{t18}{t20}{t24}</motion.div></AnimatePresence>;
    $[98] = currentIndex;
    $[99] = t18;
    $[100] = t20;
    $[101] = t24;
    $[102] = t25;
  } else {
    t25 = $[102];
  }
  let t26;
  if ($[103] !== t13 || $[104] !== t25) {
    t26 = <div className="mx-auto max-w-2xl">{t13}{t25}</div>;
    $[103] = t13;
    $[104] = t25;
    $[105] = t26;
  } else {
    t26 = $[105];
  }
  return t26;
}
function _temp4(rec, i_0) {
  return <li key={i_0} className="flex items-start gap-2 text-sm text-foreground"><span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{rec}</li>;
}
function _temp3(sum, q) {
  return sum + Math.max(...q.options.map(_temp2));
}
function _temp2(o) {
  return o.value;
}
function _temp(i) {
  return i + 1;
}